import React, { useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { AppContext } from '../../HomePage';
import { CampaignContext } from '../../App';
import { readyToDownloadCount } from '../Export/reportState';
import { REPORT_GROUPS, getVisibleReports } from './reportCatalog';
import './ReportsHub.css';

// Per-group color palette (accent / tinted background / border) so each group
// reads as its own block. Keyed by REPORT_GROUPS[].key.
const GROUP_STYLES = {
  price_comparison: { '--hub-accent': '#5b6ef5', '--hub-bg': '#f4f5ff', '--hub-border': '#e2e5fb' },
  compliance:       { '--hub-accent': '#cf7a22', '--hub-bg': '#fff7ee', '--hub-border': '#f3e2cb' },
  trends:           { '--hub-accent': '#1f9460', '--hub-bg': '#f1fbf6', '--hub-border': '#cfeede' },
  performance:      { '--hub-accent': '#8a4fd6', '--hub-bg': '#f8f3ff', '--hub-border': '#e7d9f8' },
  other:            { '--hub-accent': '#5b6472', '--hub-bg': '#f5f6f8', '--hub-border': '#e3e6ec' },
};
const DEFAULT_GROUP_STYLE = GROUP_STYLES.other;

const ReportsHub = () => {
  const { t } = React.useContext(AppContext);
  const { selectedCampaign, downloadReports, toggleDownloadReports } = React.useContext(CampaignContext);
  const [query, setQuery] = useState('');

  const visible = useMemo(() => getVisibleReports(selectedCampaign), [selectedCampaign]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter(r => {
      const label = t(r.labelKey).toLowerCase();
      const desc = t(r.descKey).toLowerCase();
      return label.includes(q) || desc.includes(q);
    });
  }, [visible, query, t]);

  const goto = (route) => { window.location = '#/' + route; };

  // Generated reports land in a side panel that is otherwise only reachable
  // from the topbar. This is the screen the customer is on when they ask for
  // one, so the way back to it belongs here too.
  const readyCount = readyToDownloadCount(downloadReports);

  const groupsToRender = REPORT_GROUPS
    .map(g => ({ group: g, reports: filtered.filter(r => r.group === g.key) }))
    .filter(x => x.reports.length > 0);

  return (
    <div className="card reports-hub">
      <div className="hub-header">
        <h2>{t('menu.reports_hub.title')}</h2>
        <p className="hub-subtitle">{t('menu.reports_hub.subtitle')}</p>
        <div className="hub-header-actions">
          <span className="p-input-icon-left hub-search">
            <i className="pi pi-search" />
            <InputText
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('menu.reports_hub.search_placeholder')}
            />
          </span>
          <Button label={t('report.my_reports')} icon="pi pi-cloud-download"
                  className="p-button-outlined hub-my-reports"
                  badge={readyCount > 0 ? String(readyCount) : null} badgeClassName="p-badge-danger"
                  onClick={() => toggleDownloadReports(true)} />
        </div>
      </div>

      {groupsToRender.length === 0 && (
        <div className="hub-empty">{t('menu.reports_hub.no_results')}</div>
      )}

      <div className="hub-groups">
        {groupsToRender.map(({ group, reports }) => (
          <div className="hub-group" key={group.key} style={GROUP_STYLES[group.key] || DEFAULT_GROUP_STYLE}>
            <div className="hub-group-title">
              <i className={group.icon} />
              <span>{t(group.titleKey)}</span>
            </div>
            <div className="hub-cards">
              {reports.map(r => (
                <button type="button" className="hub-card" key={r.key} onClick={() => goto(r.route)}>
                  <i className={`hub-card-icon ${r.icon}`} />
                  <div className="hub-card-text">
                    <div className="hub-card-title">{t(r.labelKey)}</div>
                    <div className="hub-card-desc">{t(r.descKey)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportsHub;
